import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { visualFlowAPI } from '../../services/(adminapi)/visualFlowAPI'

// Async thunks
export const fetchVisualFlows = createAsyncThunk(
  'visualFlow/fetchVisualFlows',
  async (_, { rejectWithValue }) => {
    try {
      const response = await visualFlowAPI.getAllFlows()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch visual flows')
    }
  }
)

export const fetchFlowByIntentKey = createAsyncThunk(
  'visualFlow/fetchFlowByIntentKey',
  async (intentKey, { rejectWithValue }) => {
    try {
      const response = await visualFlowAPI.getFlowByIntentKey(intentKey)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch visual flow')
    }
  }
)

function visualFlowSaveErrorMessage(error) {
  const status = error.response?.status
  if (status === 413) {
    return 'File too large for the server. Use a smaller image (under 10 MB) and try again.'
  }
  const serverMsg =
    error.response?.data?.error ||
    error.response?.data?.message ||
    (typeof error.response?.data === 'string' ? error.response.data : null)
  if (serverMsg && !String(serverMsg).includes('<html')) {
    return serverMsg
  }
  if (error.message === 'Network Error') {
    return 'Network error while uploading. Check your connection and try again.'
  }
  return 'Failed to save visual flow'
}

export const createOrUpdateFlow = createAsyncThunk(
  'visualFlow/createOrUpdateFlow',
  async (flowData, { rejectWithValue }) => {
    try {
      const response = await visualFlowAPI.createOrUpdateFlow(flowData)
      return response.data
    } catch (error) {
      return rejectWithValue(visualFlowSaveErrorMessage(error))
    }
  }
)

export const updateFlow = createAsyncThunk(
  'visualFlow/updateFlow',
  async ({ flowId, flowData }, { rejectWithValue }) => {
    try {
      const response = await visualFlowAPI.updateFlow(flowId, flowData)
      return { flowId, data: response.data }
    } catch (error) {
      return rejectWithValue(visualFlowSaveErrorMessage(error) || 'Failed to update visual flow')
    }
  }
)

export const deleteFlow = createAsyncThunk(
  'visualFlow/deleteFlow',
  async (flowId, { rejectWithValue }) => {
    try {
      const response = await visualFlowAPI.deleteFlow(flowId)
      return { flowId, response }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete visual flow')
    }
  }
)

const initialState = {
  flows: [],
  currentFlow: null,
  loading: false,
  saving: false,
  error: null,
  success: null
}

const visualFlowSlice = createSlice({
  name: 'visualFlow',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = null
    },
    setCurrentFlow: (state, action) => {
      state.currentFlow = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch visual flows
      .addCase(fetchVisualFlows.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchVisualFlows.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.success) {
          state.flows = action.payload.data || []
        }
        state.error = null
      })
      .addCase(fetchVisualFlows.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.flows = []
      })
      
      // Fetch flow by intent key
      .addCase(fetchFlowByIntentKey.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFlowByIntentKey.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.success) {
          state.currentFlow = action.payload.data
        }
        state.error = null
      })
      .addCase(fetchFlowByIntentKey.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.currentFlow = null
      })
      
      // Create or update flow
      .addCase(createOrUpdateFlow.pending, (state) => {
        state.saving = true
        state.error = null
        state.success = null
      })
      .addCase(createOrUpdateFlow.fulfilled, (state, action) => {
        state.saving = false
        if (action.payload.success) {
          state.success = 'Visual flow saved successfully'
        }
        state.error = null
      })
      .addCase(createOrUpdateFlow.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })
      
      // Update flow
      .addCase(updateFlow.pending, (state) => {
        state.saving = true
        state.error = null
        state.success = null
      })
      .addCase(updateFlow.fulfilled, (state, action) => {
        state.saving = false
        const index = state.flows.findIndex(flow => flow._id === action.payload.flowId)
        if (index !== -1 && action.payload.data?.data) {
          state.flows[index] = action.payload.data.data
        }
        state.success = 'Visual flow updated successfully'
        state.error = null
      })
      .addCase(updateFlow.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })
      
      // Delete flow
      .addCase(deleteFlow.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFlow.fulfilled, (state, action) => {
        state.loading = false
        state.flows = state.flows.filter(flow => flow._id !== action.payload.flowId)
        state.success = 'Visual flow deleted successfully'
      })
      .addCase(deleteFlow.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearSuccess, setCurrentFlow } = visualFlowSlice.actions
export default visualFlowSlice.reducer

